import { useState, useEffect } from 'react';
import { Star, Send, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, onRate, interactive = false, size = 20 }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        type="button"
        onClick={() => interactive && onRate(star)}
        className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : 'cursor-default'}
        data-testid={`star-${star}`}
      >
        <Star
          size={size}
          className={star <= rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-600'}
        />
      </button>
    ))}
  </div>
);

export const ReviewSection = () => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [page, setPage] = useState(0);
  const perPage = 3;

  useEffect(() => {
    axios.get(`${API}/reviews`).then(res => setReviews(res.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !reviewText.trim() || rating === 0) {
      toast.error('Please fill in your name, email, rating, and review.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { name: name.trim(), email: email.trim(), location: location.trim(), rating, review: reviewText.trim() });
      setSubmitted(true);
      toast.success('Thank you! Your review is pending approval.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(reviews.length / perPage);
  const visibleReviews = reviews.slice(page * perPage, (page + 1) * perPage);

  return (
    <section className="py-20 md:py-28 relative overflow-hidden border-t border-[#27272A]" data-testid="reviews-section">
      {/* DNA Background */}
      <div className="absolute inset-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/5be9d9ba-e756-4119-8306-729d4aea9d0d/images/f911a9a5e4b6c07c4d0d676dfc56f29989a457fd2fd9ef65c5e79ac4dc79091c.png"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="font-['Oswald'] text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
            Customer <span className="text-[#C41E3A]">Reviews</span>
          </h2>
          <p className="text-gray-400 text-base max-w-xl mx-auto">
            Hear from our community about their experience with Canadian Wellness & Fitness.
          </p>
        </div>

        {/* Reviews Grid */}
        {reviews.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {visibleReviews.map((r, i) => (
                <div key={r.review_id || i} className="bg-[#121212] border border-[#27272A] rounded-sm p-6 hover:border-[#D4AF37]/30 transition-colors" data-testid={`review-card-${i}`}>
                  <StarRating rating={r.rating} size={16} />
                  <p className="text-gray-300 text-sm mt-4 mb-5 leading-relaxed">"{r.review}"</p>
                  <div className="border-t border-[#27272A] pt-4 flex items-center justify-between">
                    <p className="text-white font-semibold text-sm">{r.name}</p>
                    {r.location && (
                      <p className="text-gray-500 text-xs flex items-center gap-1">
                        <MapPin size={10} /> {r.location}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mb-10">
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="text-gray-400 hover:text-white disabled:opacity-30 transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <span className="text-gray-500 text-sm">{page + 1} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="text-gray-400 hover:text-white disabled:opacity-30 transition-colors">
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-center text-gray-500 text-sm mb-10">No reviews yet. Be the first to share your experience!</p>
        )}

        {/* Write a Review */}
        {!showForm && !submitted && (
          <div className="text-center">
            <button
              onClick={() => setShowForm(true)}
              className="bg-[#C41E3A] hover:bg-[#A01830] text-white font-bold uppercase tracking-wider px-8 py-3 rounded-sm transition-all duration-300"
              data-testid="write-review-btn"
            >
              Write a Review
            </button>
          </div>
        )}

        {submitted && (
          <div className="text-center bg-[#121212] border border-[#D4AF37]/40 rounded-sm p-6 max-w-md mx-auto">
            <Star size={32} className="text-[#D4AF37] fill-[#D4AF37] mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">Thank you for your review!</p>
            <p className="text-gray-400 text-sm">It will appear on the site once approved.</p>
          </div>
        )}

        {showForm && !submitted && (
          <form onSubmit={handleSubmit} className="max-w-lg mx-auto bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="review-form">
            <h3 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-5">Share Your Experience</h3>
            
            <div className="mb-4">
              <label className="text-gray-400 text-xs uppercase tracking-wider block mb-2">Your Name *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="John D."
                className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
                data-testid="review-name"
              />
            </div>

            <div className="mb-4">
              <label className="text-gray-400 text-xs uppercase tracking-wider block mb-2">Your Email *</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
                data-testid="review-email"
              />
              <p className="text-gray-500 text-[10px] mt-1">We'll send you a 10% off promo code as a thank you!</p>
            </div>

            <div className="mb-4">
              <label className="text-gray-400 text-xs uppercase tracking-wider block mb-2">Where are you from?</label>
              <input
                type="text"
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="Calgary, AB"
                className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors"
                data-testid="review-location"
              />
            </div>

            <div className="mb-4">
              <label className="text-gray-400 text-xs uppercase tracking-wider block mb-2">Rating *</label>
              <StarRating rating={rating} onRate={setRating} interactive size={28} />
            </div>

            <div className="mb-5">
              <label className="text-gray-400 text-xs uppercase tracking-wider block mb-2">Your Experience *</label>
              <textarea
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
                placeholder="Tell us about your experience..."
                rows={4}
                className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#C41E3A] text-white text-sm px-4 py-3 rounded-sm outline-none transition-colors resize-none"
                data-testid="review-text"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-all duration-300 disabled:opacity-50"
                data-testid="submit-review-btn"
              >
                <Send size={16} />
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="border border-[#27272A] hover:border-[#C41E3A] text-gray-400 hover:text-white font-semibold uppercase tracking-wider px-5 py-3 rounded-sm transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default ReviewSection;
